import { OrganizationEntityType } from "@/lib/supabase/supabaseTypes";
import { PdfTemplateConfiguration } from "../../context/PdfTemplateConfigurationContext";
import { PdfTemplateType } from "../document-template.types";
import { Dot } from "lucide-react";
import clsx from "clsx";
import { Alignment } from "../pdf-templates/types";
import parse from "html-react-parser";
import { getConfig } from "../../context/util";
import { OrganizationsImg } from "./organizations-img.component";
import { getRtfWidth } from "../utils";

export const PdfLetterhead: React.FC<{
	organization: OrganizationEntityType;
	pdfTemplateConfiguration: PdfTemplateConfiguration;
	pdfType: PdfTemplateType;
	mwstNumber: string;
}> = ({ organization, pdfTemplateConfiguration, pdfType, mwstNumber }) => {
	const showLogo = pdfTemplateConfiguration[pdfType].showLogo;

	const generalConfig = pdfTemplateConfiguration.general;
	const pdfConfig = pdfTemplateConfiguration[pdfType] || {};

	const config = getConfig(pdfConfig, generalConfig);

	const logoSize = generalConfig.logoSize ?? 15;

	const fontSize = {
		sm: "7pt",
		md: "8pt",
		lg: "10pt",
		xl: "11pt",
		xxl: "12pt",
	};

	return (
		<div
			aria-label="Briefkopf"
			style={{
				height: "45mm",
				marginBottom: `${config.headerBodyGap}mm`,
				paddingLeft: "5mm",
				paddingRight: `${generalConfig.marginRight}mm`,
				fontSize: generalConfig.size
					? fontSize[generalConfig.size]
					: undefined,
			}}
		>
			{!config.hideIds && config.idOneLiner && (
				<div>
					{config.idRtfEnabled ? (
						<div
							style={{
								width: getRtfWidth(config.idRtfText?.width),
								overflowX: "hidden",
								height: "100%",
								maxHeight: "20mm",
							}}
						>
							{parse(config.idRtfText?.text ?? "")}
						</div>
					) : (
						<IdNumbers
							organization={organization}
							logoSize={logoSize ?? 18}
							oneLiner={true}
							idAlignment={"left"}
							mwstNumber={mwstNumber ?? ""}
						/>
					)}
				</div>
			)}
			<div
				aria-label="Oberhalb der Trennlinie mit Firmenlogo, Adresse, Kontaktinformationen und Mitgliedschaftsstatus"
				style={{
					borderBottom:
						config.showBorder == true ? "1px solid black" : "",

					// space between vertically
					display: "flex",
					flexDirection: "column",
					justifyContent: "space-between",
					paddingBottom: "1mm",
					height: `${logoSize + 21}mm`,
				}}
			>
				{showLogo && (
					<div
						style={{
							height: `${(logoSize ?? 20) + 7}mm`,
							paddingTop: "4mm",
							paddingBottom: "3mm",
							overflow: "hidden",
							display: "flex",
							justifyContent:
								generalConfig.logoAlignment ?? "left",
						}}
					>
						<img
							alt="logo"
							style={{
								height: `${logoSize ?? 20}mm`,
								maxHeight: "100%",
								maxWidth: "100%",
								objectFit: "contain",
							}}
							src={generalConfig.logoImgurl}
						/>
					</div>
				)}
				{!config.hideLabAddress && (
					<div>
						{config.addressRtfEnabled ? (
							<div
								style={{
									width: getRtfWidth(
										config.addressRtfText?.width
									),
									...(config.addressRtfText?.text.includes(
										"align-center"
									) && {
										paddingRight: `${
											generalConfig.marginLeft + 5
										}mm`,
									}), // to set it perfectly centered
								}}
							>
								{parse(config.addressRtfText?.text ?? "")}
							</div>
						) : (
							<Address
								organization={organization}
								email={generalConfig?.email ?? ""}
								oneLiner={config.oneLiner ?? false}
								showBorder={config.showBorder ?? false}
								side={config.addressAlignment}
								website={generalConfig?.website ?? ""}
								config={config}
								generalConfig={generalConfig}
								fontSize={fontSize}
							/>
						)}
					</div>
				)}
				{!config.hideMembers &&
					config.organizationsImgAlignment === "left" && (
						<div
							aria-label="Kontaktinformationen und Mitgliedschaftsstatus"
							style={{
								display: "flex",
								flexDirection: "row",
								justifyContent: "space-between",
								paddingBottom: "1mm",
								fontSize: fontSize[generalConfig.size ?? "lg"],
								alignItems: "center",
							}}
						>
							{config.organizationsImgPosition == "top" && (
								<OrganizationsImg
									config={config}
									generalConfig={generalConfig}
								/>
							)}
						</div>
					)}
			</div>
			{!config.hideIds && !config.idOneLiner && (
				<div style={{ marginTop: `${config.idNumbersGap}mm` }}>
					{config.idRtfEnabled ? (
						<div
							style={{
								width: getRtfWidth(config.idRtfText?.width),
								overflowX: "hidden",
								height: "100%",
								whiteSpace: "pre-wrap",
							}}
						>
							{parse(config.idRtfText?.text ?? "")}
						</div>
					) : (
						<IdNumbers
							organization={organization}
							logoSize={logoSize ?? 18}
							oneLiner={config.idOneLiner}
							idAlignment={config.idAlignment}
							showBorder={config.showBorder}
							mwstNumber={mwstNumber ?? ""}
						/>
					)}
				</div>
			)}
		</div>
	);
};

const Address = ({
	organization,
	oneLiner,
	email,
	website,
	side,
	config,
	generalConfig,
	showBorder,
	fontSize,
}: {
	organization: OrganizationEntityType;
	email: string;
	oneLiner: boolean;
	showBorder?: boolean;
	website: string;
	side?: "left" | "right" | "center";
	config?: any;
	generalConfig?: any;
	fontSize?: any;
}) => {
	return (
		<div
			className={config.senderAddressUpShift == null ? "mt-2" : undefined}
			style={{
				fontSize:
					fontSize?.[
						config.senderAddressFontSize ??
							generalConfig.size ??
							"lg"
					],
				...(config.senderAddressUpShift != null && {
					marginTop: `${-config.senderAddressUpShift}mm`,
				}),
			}}
		>
			{oneLiner ? (
				<div className="flex flex-col gap-1  items-center">
					<p className="flex  items-center whitespace-nowrap gap-0.5">
						{organization?.title_or_company_name ||
							organization?.name}
					</p>
					<div className="flex flex-row gap-0 text-sm items-center justify-between w-full">
						<div className="flex flex-row gap-0 text-sm items-center">
							{organization?.name_or_additional_information && (
								<p className="flex items-center whitespace-nowrap gap-0.5">
									{
										organization?.name_or_additional_information
									}
								</p>
							)}
							{organization?.street && (
								<p className="flex items-center whitespace-nowrap gap-0.5">
									<Dot />
									{organization?.street}
								</p>
							)}
							{organization?.city && (
								<p className="flex items-center whitespace-nowrap gap-0.5">
									<Dot />
									{organization?.postal_code}{" "}
									{organization?.city}
								</p>
							)}
							{organization?.phone_and_fax && (
								<p className="flex items-center whitespace-nowrap gap-0.5">
									<Dot />
									{organization?.phone_and_fax}
								</p>
							)}
							{email && (
								<p className="flex items-center whitespace-nowrap gap-0.5">
									<Dot />
									{email}
								</p>
							)}
							{website && (
								<p className="flex items-center whitespace-nowrap gap-0.5">
									<Dot />
									{website}
								</p>
							)}
							{config.organizationsImgAlignment === "right" &&
								config &&
								!config.hideMembers &&
								config.organizationsImgPosition == "top" && (
									<OrganizationsImg
										config={config}
										generalConfig={generalConfig}
									/>
								)}
						</div>
					</div>
				</div>
			) : (
				<div
					className={clsx("w-full flex flex-col", {
						"items-end": side === "right",
						"items-start": side === "left",
						"items-center": side === "center",
					})}
				>
					<div aria-label="Firmenadresse">
						{organization?.title_or_company_name ||
							(organization?.name && (
								<p>
									{organization?.title_or_company_name ||
										organization?.name}
								</p>
							))}
						{organization?.name_or_additional_information && (
							<p>
								{organization?.name_or_additional_information}
							</p>
						)}
						{organization?.street && <p>{organization?.street}</p>}
						{organization?.postal_code && organization?.city && (
							<p>
								{organization?.postal_code} {organization?.city}
							</p>
						)}
					</div>
					<div
						style={{
							display: "flex",
							flexDirection: "row",
							alignItems: "flex-end",
							justifyContent: "space-between",
							width: "100%",
							...(config.senderAddressUpShift != null && {
								marginTop: `${config.senderAddressUpShift}mm`,
							}),
						}}
					>
						<div
							style={{
								display: "flex",
								flexDirection: "row",
								alignItems: "flex-end",
								justifyContent: "flex-start",
								width: "100%",
							}}
						>
							{organization &&
								organization.phone_and_fax &&
								organization.phone_and_fax}
							{email && (
								<>
									<Dot size={16} />
									{email}
								</>
							)}
							{website && (
								<>
									<Dot size={16} />
									{website}
								</>
							)}
						</div>
						{config.organizationsImgAlignment === "right" &&
							config &&
							!config.hideMembers &&
							config.organizationsImgPosition == "top" && (
								<OrganizationsImg
									config={config}
									generalConfig={generalConfig}
								/>
							)}
					</div>
				</div>
			)}
		</div>
	);
};

const IdNumbers = ({
	organization,
	oneLiner,
	idAlignment,
	showBorder,
	mwstNumber,
}: {
	organization: OrganizationEntityType;
	oneLiner?: boolean;
	showBorder?: boolean;
	logoSize: number;
	idAlignment?: Omit<Alignment, "center">;
	mwstNumber: string;
}) => {
	const alignmentTerms = {
		left: "start",
		right: "end",
	};
	return (
		<>
			{oneLiner ? (
				<div
					className="flex text-[8px] gap-0 h-[0mm] items-center"
					style={{
						justifyContent: (idAlignment ?? "left") as
							| "left"
							| "right",
					}}
				>
					{mwstNumber && (
						<p className="flex items-center whitespace-nowrap gap-0.5">
							MwSt.-Nummer: {mwstNumber}
						</p>
					)}
					{organization.gln && (
						<p className="flex items-center whitespace-nowrap gap-0.5">
							<Dot />
							GLN: {organization.gln}
						</p>
					)}
				</div>
			) : (
				<div
					className={`flex w-full ${
						showBorder
							? "flex-row gap-4 text-[10px]"
							: "flex-col text-xs"
					} gap-0`}
					style={{
						alignItems:
							alignmentTerms[
								(idAlignment ?? "left") as "left" | "right"
							],
					}}
				>
					{mwstNumber && (
						<p className="flex items-center whitespace-nowrap gap-0.5">
							MwSt.-Nummer: {mwstNumber}
						</p>
					)}
					{organization.gln && (
						<p className="flex items-center whitespace-nowrap gap-0.5">
							GLN: {organization.gln}
						</p>
					)}
				</div>
			)}
		</>
	);
};
